/**
 * Releases streamed text at a reading pace.
 *
 * A model can write a whole reply in half a second, and tokens that arrive in
 * one burst look exactly like a reply that arrived whole. The pacer holds what
 * has arrived and lets it out steadily — fast enough never to fall far behind,
 * since the pace grows with the backlog — so the visitor sees it being written.
 */
/** Characters per second when nothing is waiting. */
const BASE_CHARS_PER_SECOND = 90;
/** Whatever the backlog, it is caught up within about this long. */
const CATCH_UP_SECONDS = 1.2;
const TICK_MS = 30;
export class TextPacer {
    release;
    buffer = '';
    timer;
    drained = [];
    constructor(release) {
        this.release = release;
    }
    push(text) {
        if (!text)
            return;
        this.buffer += text;
        this.timer ??= setInterval(() => this.tick(), TICK_MS);
    }
    /** Resolves once everything pushed so far has been released. */
    drain() {
        if (!this.buffer)
            return Promise.resolve();
        return new Promise((resolve) => this.drained.push(resolve));
    }
    /** Release everything now (the reply is complete, or the bubble is gone). */
    flush() {
        if (this.buffer)
            this.release(this.buffer);
        this.buffer = '';
        this.stop();
    }
    /** Drop what is held without releasing it (a retried turn starts over). */
    clear() {
        this.buffer = '';
        this.stop();
    }
    tick() {
        const perTick = Math.max((BASE_CHARS_PER_SECOND * TICK_MS) / 1000, (this.buffer.length * TICK_MS) / (CATCH_UP_SECONDS * 1000));
        let take = Math.max(1, Math.round(perTick));
        // Never split a surrogate pair (an emoji) across two releases.
        const code = this.buffer.charCodeAt(take - 1);
        if (code >= 0xd800 && code <= 0xdbff)
            take += 1;
        const out = this.buffer.slice(0, take);
        this.buffer = this.buffer.slice(take);
        if (out)
            this.release(out);
        if (!this.buffer)
            this.stop();
    }
    stop() {
        if (this.timer)
            clearInterval(this.timer);
        this.timer = undefined;
        const waiting = this.drained;
        this.drained = [];
        for (const resolve of waiting)
            resolve();
    }
}
